import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, extractErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Circle, FileText, PenLine, Download, ShieldCheck } from "lucide-react";

// IRS-recognized entity classifications (Form W-9 / 8832)
const ENTITY_TYPES = [
  "Sole Proprietor / Individual", "C Corporation", "S Corporation", "Partnership",
  "Limited Liability Company (LLC)", "Trust / Estate", "Nonprofit / Tax-Exempt Organization", "Government Entity",
];
const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
  "District of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
  "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island",
  "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming",
];

const STATUS_META = {
  invited: { label: "Invited", cls: "bg-slate-100 text-slate-600" },
  onboarding: { label: "Onboarding", cls: "bg-sky-50 text-sky-700" },
  agreement_pending: { label: "Agreement Pending", cls: "bg-amber-50 text-amber-700" },
  billing_pending: { label: "Billing Pending", cls: "bg-amber-50 text-amber-700" },
  pending_approval: { label: "Pending ARPS Approval", cls: "bg-indigo-50 text-indigo-700" },
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-700" },
};
const PROFILE_FIELDS = [
  ["legal_business_name", "Legal business name *"], ["dba", "DBA"], ["entity_type", "Entity type *"],
  ["state_of_formation", "State of formation *"], ["ein", "EIN / Federal Tax ID"], ["state_tax_id", "State registration / tax ID"],
  ["professional_license", "Professional license"], ["website", "Website"],
  ["physical_address", "Physical address"], ["mailing_address", "Mailing address"], ["billing_address", "Billing address"],
  ["primary_contact_name", "Primary contact *"], ["primary_contact_email", "Primary contact email *"], ["primary_contact_phone", "Primary contact phone *"],
  ["billing_contact", "Billing / AP contact"], ["payment_terms", "Payment terms"],
];

function Row({ ok, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
      <span className={ok ? "text-slate-800" : "text-slate-500"}>{label}</span>
    </div>
  );
}

export default function ClientOnboarding() {
  const nav = useNavigate();
  const [ob, setOb] = useState(null);
  const [prof, setProf] = useState({});
  const [sig, setSig] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/client/onboarding").then(({ data }) => {
    setOb(data); setProf(data.business_profile || {});
  }).catch(() => toast.error("Failed to load onboarding"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!ob) return <div className="mx-auto max-w-4xl p-6 text-slate-400">Loading…</div>;
  const r = ob.readiness;
  const m = STATUS_META[ob.client_status] || STATUS_META.onboarding;

  const saveProfile = async () => {
    setBusy(true);
    try {
      const payload = {};
      PROFILE_FIELDS.forEach(([k]) => { if (prof[k] !== undefined) payload[k] = prof[k]; });
      await api.put("/client/business-profile", payload);
      toast.success("Business profile saved"); await load();
    } catch (e) { toast.error(extractErrorMessage(e, "Failed to save profile")); }
    finally { setBusy(false); }
  };

  const openBlob = async (url) => {
    // Open the tab synchronously inside the click gesture so browsers don't popup-block it after the await.
    const win = window.open("", "_blank");
    try {
      const resp = await api.get(url, { responseType: "blob" });
      const b = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      if (win) win.location = b; else window.location.assign(b);
      setTimeout(() => URL.revokeObjectURL(b), 60000);
    } catch { if (win) win.close(); toast.error("Unable to open document"); }
  };

  const signDoc = async (doc) => {
    const signature = (sig[doc.key] || "").trim();
    if (!signature) { toast.error("Type your full legal name to sign"); return; }
    setBusy(true);
    try {
      const ep = doc.key === "terms_conditions" ? "/terms/accept" : `/onboarding-documents/${doc.key}/accept`;
      await api.post(ep, { signature });
      toast.success(`${doc.title} executed`); await load();
    } catch (e) { toast.error(extractErrorMessage(e, "Failed to sign")); }
    finally { setBusy(false); }
  };

  const submitForReview = async () => {
    setBusy(true);
    try {
      await api.post("/client/submit-for-review");
      toast.success("Submitted for ARPS review");
      await load();
    } catch (e) { toast.error(extractErrorMessage(e, "Unable to submit for review")); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="client-onboarding-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Client onboarding</h2>
          <p className="text-slate-500">Complete your profile, sign the required documents, then ARPS activates your account.</p>
        </div>
        <span data-testid="onboarding-status-badge" className={`rounded-full px-3 py-1 text-xs font-semibold ${m.cls}`}>{m.label}</span>
      </div>

      {ob.client_status === "active" ? (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800" data-testid="onboarding-active-banner">
          <ShieldCheck className="h-5 w-5" /> Your account is active. You can submit service orders.
        </div>
      ) : ob.client_status === "pending_approval" ? (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5" data-testid="onboarding-pending-approval-banner">
          <div className="flex items-center gap-2 text-indigo-800"><ShieldCheck className="h-5 w-5" /><p className="font-semibold">Submitted for ARPS review</p></div>
          <p className="mt-2 text-sm text-indigo-800">Enter your service request now so it's ready the moment ARPS approves you. You won't be charged until after approval.</p>
          <Button data-testid="enter-service-request-btn" onClick={() => nav("/client/start")} className="mt-3 bg-indigo-600 text-white hover:bg-indigo-700">Enter service request</Button>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-base font-bold text-slate-900">Steps to submit for review</h3>
          <div className="mt-3 space-y-2">
            <Row ok={r.info_complete} label="Account information complete" />
            <Row ok={r.business_profile_complete} label="Business profile complete" />
            <Row ok={r.documents_accepted} label={`All documents signed (${(ob.documents || []).filter((d) => d.accepted).length}/${(ob.documents || []).length})`} />
          </div>
          <p className="mt-3 text-xs text-slate-400">When these are complete, submit for ARPS review. You can then enter your service request right away; ARPS reviews and approves before any payment.</p>
          <Button data-testid="submit-for-review-btn" onClick={submitForReview}
            disabled={busy || !(r.info_complete && r.business_profile_complete && r.documents_accepted)}
            className="mt-3 bg-indigo-600 text-white hover:bg-indigo-700">Submit for review</Button>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-display text-base font-bold text-slate-900">Business profile</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {PROFILE_FIELDS.map(([k, label]) => (
            <div key={k}>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
              {k === "entity_type" || k === "state_of_formation" ? (
                <Select value={prof[k] || ""} onValueChange={(v) => setProf((s) => ({ ...s, [k]: v }))}>
                  <SelectTrigger data-testid={`bp-${k}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {(k === "entity_type" ? ENTITY_TYPES : US_STATES).map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid={`bp-${k}`} value={prof[k] || ""} onChange={(e) => setProf((s) => ({ ...s, [k]: e.target.value }))} />
              )}
            </div>
          ))}
        </div>
        <Button data-testid="save-profile-btn" onClick={saveProfile} disabled={busy} className="mt-4">Save business profile</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-display text-base font-bold text-slate-900">Required documents</h3>
        <div className="mt-3 space-y-3">
          {(ob.documents || []).map((doc) => (
            <div key={doc.key} data-testid={`doc-${doc.key}`} className="rounded-lg border border-slate-100 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-400" />
                  <span className="font-semibold text-slate-900">{doc.title}</span>
                  <span className="text-xs text-slate-400">v{doc.version}</span>
                </div>
                {doc.accepted
                  ? <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Executed</span>
                  : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Awaiting signature</span>}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button data-testid={`preview-${doc.key}`} variant="outline" size="sm" onClick={() => openBlob(`/onboarding-documents/${doc.key}/preview`)}>
                  <FileText className="mr-1 h-3.5 w-3.5" /> Review
                </Button>
                {doc.accepted ? (
                  <Button data-testid={`download-${doc.key}`} variant="outline" size="sm" onClick={() => openBlob(`/files/${doc.document_id}`)}>
                    <Download className="mr-1 h-3.5 w-3.5" /> Executed PDF
                  </Button>
                ) : (
                  <>
                    <Input data-testid={`sign-input-${doc.key}`} placeholder="Type full legal name to sign" value={sig[doc.key] || ""}
                      onChange={(e) => setSig((s) => ({ ...s, [doc.key]: e.target.value }))} className="h-8 w-56" />
                    <Button data-testid={`sign-${doc.key}`} size="sm" disabled={busy} onClick={() => signDoc(doc)} className="bg-emerald-600 text-white hover:bg-emerald-700">
                      <PenLine className="mr-1 h-3.5 w-3.5" /> Sign & accept
                    </Button>
                  </>
                )}
              </div>
              {doc.accepted && <p className="mt-2 text-xs text-slate-400">Signed by {doc.signer || doc.accepting_user} · {doc.accepted_at ? new Date(doc.accepted_at).toLocaleString() : ""}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
